import type {
  AdminDanceIdentity,
  AdminDailyFamilyPlays,
  AdminDanceRetention,
  AdminDashboardData,
  AdminFamilyDances
} from "@recital-master/shared";
import { PLAYBACK_BUCKET_SECONDS } from "@recital-master/shared";

import { DEV_MEDIA_BASE } from "@/data/catalog";

/**
 * Deterministic design-preview data. Dance identity/title/style values are
 * copied from fixtures/synthetic-season/generated/catalog.json so admin
 * screenshots use the same fictional season as the rest of the app.
 */
const fixtureDances = [
  ["recital-1-part-1-001", "recital-1", "Opening Jazz 1", "Jazz", "Recital 1"],
  ["recital-1-part-1-002", "recital-1", "Tiny Tap Parade 2", "Tap", "Recital 1"],
  ["recital-1-part-1-003", "recital-1", "Moonlight Ballet 3", "Ballet", "Recital 1"],
  ["recital-1-part-1-004", "recital-1", "Hip Hop Heroes 4", "Hip Hop", "Recital 1"],
  ["recital-1-part-1-005", "recital-1", "Finale Company 5", "Musical Theatre", "Recital 1"],
  ["recital-2-part-1-001", "recital-2", "Sunrise Ballet 1", "Jazz", "Recital 2"],
  ["recital-2-part-1-002", "recital-2", "Rhythm Lab 2", "Tap", "Recital 2"],
  ["recital-2-part-1-003", "recital-2", "Acro Sparks 3", "Ballet", "Recital 2"],
  ["recital-2-part-1-004", "recital-2", "Contemporary Blue 4", "Hip Hop", "Recital 2"],
  ["recital-2-part-1-005", "recital-2", "Street Beat 5", "Musical Theatre", "Recital 2"],
  ["recital-1-part-2-001", "recital-1", "Golden Hour 1", "Jazz", "Recital 1"],
  ["recital-1-part-2-002", "recital-1", "Broadway Lights 2", "Tap", "Recital 1"],
  ["recital-1-part-2-003", "recital-1", "Electric Avenue 3", "Ballet", "Recital 1"],
  ["recital-1-part-2-004", "recital-1", "Velvet Sky 4", "Hip Hop", "Recital 1"],
  ["recital-1-part-2-005", "recital-1", "Firefly Waltz 5", "Musical Theatre", "Recital 1"],
  ["recital-1-part-2-006", "recital-1", "Opening Jazz 6", "Contemporary", "Recital 1"],
  ["recital-1-part-2-007", "recital-1", "Tiny Tap Parade 7", "Acro", "Recital 1"],
  ["recital-1-part-2-008", "recital-1", "Moonlight Ballet 8", "Lyrical", "Recital 1"]
] as const;

const plays = [312, 274, 239, 205, 171, 139, 111, 86, 63, 44, 37, 31, 26, 22, 18, 14, 10, 7];
const completion = [
  0.91, 0.84, 0.88, 0.79, 0.73, 0.76, 0.69, 0.65, 0.61,
  0.56, 0.58, 0.54, 0.52, 0.49, 0.47, 0.44, 0.41, 0.38
];
const firstNames = [
  "ava", "mia", "olivia", "emma", "sophia", "amelia", "charlotte", "isla", "harper", "ella",
  "lucas", "liam", "noah", "ethan", "theo", "jack", "henry", "leo", "owen", "miles"
];
const lastNames = [
  "martin", "chen", "patel", "thompson", "rodriguez", "wilson", "nguyen", "anderson", "brown", "taylor",
  "campbell", "singh"
];
const SAMPLE_NOW = Date.parse("2026-07-18T15:30:00.000Z");
const SAMPLE_STORYBOARD_VTT_URL =
  `${DEV_MEDIA_BASE}/media/nights/recital-1/dances/` +
  "recital-1-part-1-001/storyboard.vtt";

const topDances: AdminDashboardData["topDances"] = fixtureDances.map(
  ([danceId, performanceId, title, style, performanceTitle], index) => {
    const durationSeconds = 108 + ((index * 17) % 83);
    return {
      danceId,
      performanceId,
      recitalId: "season-2026-synthetic",
      title,
      styles: [style],
      performanceTitle,
      durationSeconds,
      plays: plays[index],
      watchHours: Math.round((plays[index] * durationSeconds * completion[index] / 3600) * 10) / 10,
      completionRate: completion[index],
      lastWatchedAt: new Date(SAMPLE_NOW - (index + 1) * 73 * 60 * 1000).toISOString()
    };
  }
).sort(
  (left, right) => right.watchHours - left.watchHours || right.plays - left.plays
);

const families: AdminDashboardData["families"] = Array.from({ length: 120 }, (_, index) => {
  const first = firstNames[(index * 7) % firstNames.length];
  const last = lastNames[(index * 5 + Math.floor(index / firstNames.length)) % lastNames.length];
  const inactive = index >= 104;
  const lastActiveAt = new Date(SAMPLE_NOW - (index * index + 2) * 47 * 60 * 1000).toISOString();
  const lastWatchedAt = new Date(Date.parse(lastActiveAt) - (index % 4 + 1) * 31 * 60 * 1000)
    .toISOString();
  const statuses = ["active", "active", "active", "canceling", "none", "active", "past_due"];
  const dancerCount = index % 4;
  const plays7d = index % 5 === 0 ? 0 : 2 + (index * 3) % 18;
  const plays30d = plays7d + 3 + (index * 5) % 24;
  const plays365d = plays30d + 8 + (index * 7) % 60;
  const myDancers = Array.from({ length: dancerCount }, (_, dancerIndex) => {
    const dancerFirst = firstNames[(index * 3 + dancerIndex * 5 + 4) % firstNames.length];
    const dancerLast = lastNames[(index * 2 + dancerIndex * 3 + 1) % lastNames.length];
    return {
      dancerId: `sample-dancer-${index + 1}-${dancerIndex + 1}`,
      name: `${dancerFirst[0].toUpperCase()}${dancerFirst.slice(1)} ${dancerLast[0].toUpperCase()}${dancerLast.slice(1)}`,
      slug: `${dancerFirst}-${dancerLast}`,
      recitalId: "season-2026-synthetic",
      savedAt: new Date(SAMPLE_NOW - (dancerIndex + 1) * 60_000).toISOString()
    };
  });
  return {
    familyAccountId: `sample-family-${String(index + 1).padStart(3, "0")}`,
    email: `${first}.${last}${index >= 20 ? index + 1 : ""}@example.com`,
    accessUntil: inactive
      ? new Date(SAMPLE_NOW - (index - 103) * 8 * 24 * 60 * 60 * 1000).toISOString()
      : new Date(SAMPLE_NOW + (35 + (index * 19) % 310) * 24 * 60 * 60 * 1000).toISOString(),
    billingStatus: statuses[index % statuses.length],
    deviceCount: index % 11 === 0 ? 1 : index % 5 === 0 ? 3 : 2,
    swapsRemaining: index % 17 === 0 ? 0 : index % 7 === 0 ? 1 : 2,
    lastActiveAt,
    lastWatchedAt,
    watchHours: Math.round((0.6 + ((index * 37) % 148) / 10) * 10) / 10,
    myDancers,
    activity: {
      plays7d,
      watchHours7d: Math.round(plays7d * (0.04 + (index % 3) * 0.01) * 10) / 10,
      plays30d,
      watchHours30d: Math.round(plays30d * (0.04 + (index % 4) * 0.01) * 10) / 10,
      plays365d,
      watchHours365d: Math.round(plays365d * (0.04 + (index % 5) * 0.01) * 10) / 10
    }
  };
});

// A release spike that decays into a long tail, with today still in progress —
// the shape the trend row exists to show.
const sampleDailyHours = [0.4, 1.1, 9.6, 22.4, 17.3, 12.1, 8.7, 6.2, 5.4, 4.1, 3.6, 2.9, 3.2, 1.8];
const sampleDailyFamilies = [2, 4, 21, 38, 31, 26, 19, 15, 13, 11, 9, 8, 10, 7];
const sampleDailyPlays = [6, 17, 146, 328, 251, 189, 132, 97, 81, 63, 54, 43, 49, 27];
const sampleDailyWatch: NonNullable<AdminDashboardData["dailyWatch"]> =
  sampleDailyHours.map((watchHours, index) => {
    const familyCount = sampleDailyFamilies[index];
    const dayFamilies = families.slice(0, familyCount);
    const weightTotal = dayFamilies.reduce((sum, _, familyIndex) =>
      sum + (familyCount - familyIndex), 0
    );
    return {
      date: new Date(SAMPLE_NOW - (sampleDailyHours.length - 1 - index) * 86_400_000)
        .toISOString()
        .slice(0, 10),
      familyCount,
      playCount: sampleDailyPlays[index],
      watchHours,
      families: dayFamilies.map((family, familyIndex) => {
        const familyPlayCount = Math.max(
          1,
          Math.round(sampleDailyPlays[index] * (familyCount - familyIndex) / weightTotal)
        );
        const familyWatchHours = Math.round(
          watchHours * (familyCount - familyIndex) / weightTotal * 1000
        ) / 1000;
        const danceCount = Math.min(3, familyPlayCount);
        return {
          familyAccountId: family.familyAccountId,
          email: family.email,
          playCount: familyPlayCount,
          watchHours: familyWatchHours,
          dances: Array.from({ length: danceCount }, (_, danceIndex) => {
            const source = topDances[(familyIndex * 2 + danceIndex + index) % topDances.length];
            return {
              ...source,
              plays: Math.max(1, Math.floor(familyPlayCount / danceCount)),
              watchHours: Math.round(familyWatchHours / danceCount * 1000) / 1000,
              lastWatchedAt: new Date(
                SAMPLE_NOW - (sampleDailyHours.length - 1 - index) * 86_400_000 -
                danceIndex * 17 * 60_000
              ).toISOString()
            };
          })
        };
      })
    };
  });

const sampleEmailMessages: NonNullable<AdminDashboardData["emailActivity"]>["messages"] =
  Array.from({ length: 28 }, (_, index) => {
    const recipient = index < 20 ? families[index].email :
      `invited.family${index - 19}@example.com`;
    const createdAt = new Date(SAMPLE_NOW - index * 37 * 60_000).toISOString();
    const delivered = index % 13 !== 0;
    const humanOpenCount = delivered && index % 3 !== 0 ? 1 + (index % 2) : 0;
    const automatedOpenCount = delivered && index % 4 === 0 ? 1 : 0;
    const humanClickCount = humanOpenCount > 0 && index % 4 === 1 ? 1 : 0;
    const openedAt = humanOpenCount > 0 ?
      new Date(Date.parse(createdAt) + 14 * 60_000).toISOString() :
      null;
    const clickedAt = humanClickCount > 0 ?
      new Date(Date.parse(createdAt) + 19 * 60_000).toISOString() :
      null;
    return {
      id: `sample-email-${index + 1}`,
      recipient,
      subject: index < 24 ? "Your recital is ready when you are" : "Your sign-in link",
      template: index < 24 ? "campaign" : "sign-in",
      campaign: index < 24 ? "showstoppers-reminder-2026" : "sign-in-link",
      createdAt,
      acceptedAt: createdAt,
      deliveredAt: delivered ?
        new Date(Date.parse(createdAt) + 45_000).toISOString() :
        null,
      firstOpenedAt: openedAt,
      lastOpenedAt: openedAt,
      humanOpenCount,
      automatedOpenCount,
      firstClickedAt: clickedAt,
      lastClickedAt: clickedAt,
      humanClickCount,
      automatedClickCount: 0,
      temporaryFailedAt: null,
      permanentlyFailedAt: delivered ? null :
        new Date(Date.parse(createdAt) + 90_000).toISOString(),
      failureReason: delivered ? null : "Mailbox unavailable",
      complainedAt: null,
      unsubscribedAt: null,
      signedInAt: index < 20 ? families[index].lastActiveAt : null
    };
  });

const sampleHumanEngaged = (message: typeof sampleEmailMessages[number]) =>
  message.humanOpenCount > 0 || message.humanClickCount > 0;

export const sampleAdminDashboard: AdminDashboardData = {
  generatedAt: new Date(SAMPLE_NOW).toISOString(),
  totals: {
    families: families.length,
    activeAccess: 104,
    watchingNow: 7,
    plays7d: 786,
    watchHours7d: 27.2,
    today: {
      familyCount: sampleDailyFamilies[13],
      watchHours: sampleDailyHours[13],
      timeZone: "America/Winnipeg"
    },
    yesterday: {
      familyCount: sampleDailyFamilies[12],
      watchHours: sampleDailyHours[12]
    },
    watchHoursPrev7d: 71.6,
    familiesEverWatched: 107,
    unclaimedInvites: 9,
    lastWatchedAt: new Date(SAMPLE_NOW - 12 * 60 * 1000).toISOString()
  },
  dailyWatch: sampleDailyWatch,
  topDances,
  playTesters: {
    uniqueCount: 14,
    submissions: families.slice(0, 14).map((family, index) => ({
      familyAccountId: family.familyAccountId,
      purchaseEmail: family.email,
      googleEmail: `play.tester${index + 1}@gmail.com`,
      submittedAt: new Date(SAMPLE_NOW - index * 3_600_000).toISOString()
    }))
  },
  emailActivity: {
    totals: {
      sent: sampleEmailMessages.filter((message) => message.acceptedAt != null).length,
      delivered: sampleEmailMessages.filter((message) => message.deliveredAt != null).length,
      opened: sampleEmailMessages.filter(sampleHumanEngaged).length,
      automatedOnly: sampleEmailMessages.filter((message) =>
        !sampleHumanEngaged(message) && message.automatedOpenCount > 0
      ).length,
      clicked: sampleEmailMessages.filter((message) => message.humanClickCount > 0).length,
      failed: sampleEmailMessages.filter((message) => message.permanentlyFailedAt != null).length,
      openedNotSignedIn: sampleEmailMessages.filter((message) =>
        sampleHumanEngaged(message) && message.signedInAt == null
      ).length
    },
    messages: sampleEmailMessages
  },
  families
};

export function sampleAdminFamilyDances(familyAccountId: string): AdminFamilyDances {
  const familyIndex = Math.max(0, Number(familyAccountId.match(/(\d+)$/)?.[1] ?? 1) - 1);
  const count = 3 + (familyIndex % 4);
  const dances = Array.from({ length: count }, (_, index) => {
    const dance = topDances[(familyIndex * 3 + index * 3) % topDances.length];
    const familyPlays = Math.max(1, 8 - index - (familyIndex % 3));
    const completionRate = Math.max(0.42, dance.completionRate - (familyIndex % 5) * 0.04);
    return {
      ...dance,
      plays: familyPlays,
      watchHours: Math.round(
        (familyPlays * dance.durationSeconds * completionRate / 3600) * 10
      ) / 10,
      completionRate,
      lastWatchedAt: new Date(
        SAMPLE_NOW - (familyIndex + 1) * (index + 2) * 41 * 60 * 1000
      ).toISOString()
    };
  });
  return { familyAccountId, dances };
}

function sampleRetentionBuckets(
  durationSeconds: number,
  plays: number,
  variation = 0
): AdminDanceRetention["buckets"] {
  const bucketCount = Math.ceil(durationSeconds / PLAYBACK_BUCKET_SECONDS);
  return Array.from({ length: bucketCount }, (_, index) => {
    const progress = bucketCount <= 1 ? 0 : index / (bucketCount - 1);
    const decay = 0.97 - progress * (0.28 + variation) - progress * progress * 0.08;
    const highlight = 0.32 * Math.exp(-Math.pow((progress - 0.64) / 0.055, 2));
    const endingDrop = progress > 0.9 ? (progress - 0.9) * 2.1 : 0;
    const ratio = Math.max(0.36, Math.min(1.22, decay + highlight - endingDrop));
    return {
      startSecond: index * PLAYBACK_BUCKET_SECONDS,
      watchMoments: Math.round(plays * ratio)
    };
  });
}

export function sampleAdminDanceRetention(
  identity: AdminDanceIdentity,
  familyAccountId?: string
): AdminDanceRetention {
  const source = familyAccountId ? sampleAdminFamilyDances(familyAccountId).dances : topDances;
  const dance = source.find((entry) =>
    entry.danceId === identity.danceId &&
    entry.performanceId === identity.performanceId &&
    entry.recitalId === identity.recitalId
  ) ?? source[0] ?? topDances[0];
  const buckets = sampleRetentionBuckets(dance.durationSeconds, dance.plays);
  const retentionFamilies = familyAccountId
    ? undefined
    : families.slice(0, 8).map((family, index) => {
        const plays = 1 + (index % 3);
        return {
          familyAccountId: family.familyAccountId,
          email: family.email,
          plays,
          buckets: sampleRetentionBuckets(
            dance.durationSeconds,
            plays,
            (index % 4) * 0.045
          )
        };
      });
  return {
    danceId: dance.danceId,
    durationSeconds: dance.durationSeconds,
    plays: dance.plays,
    storyboardVttUrl: SAMPLE_STORYBOARD_VTT_URL,
    buckets,
    families: retentionFamilies
  };
}

export function sampleAdminDailyFamilyPlays(
  date: string,
  familyAccountId: string,
  dances: AdminDashboardData["topDances"]
): AdminDailyFamilyPlays {
  return {
    date,
    familyAccountId,
    dances: dances.map((dance, danceIndex) => {
      const playbacks = Array.from({length: Math.max(1, dance.plays)}, (_, playIndex) => {
        const start = (danceIndex * 13 + playIndex * 19) % Math.max(1, dance.durationSeconds / 3);
        const end = Math.min(
          dance.durationSeconds,
          start + dance.durationSeconds * (0.48 + (playIndex % 3) * 0.16)
        );
        const buckets = Array.from({length: Math.ceil(dance.durationSeconds)}, (_, second) => {
          const watched = second >= start && second <= end && (second + playIndex) % 29 !== 0;
          return {startSecond: second, watchMoments: watched ? 1 : 0, viewers: watched ? 1 : 0};
        });
        const watchedSeconds = buckets.filter((bucket) => bucket.watchMoments === 1).length;
        const newest = Date.parse(dance.lastWatchedAt ?? `${date}T16:00:00.000Z`);
        return {
          playId: `sample-${familyAccountId}-${dance.danceId}-${playIndex + 1}`,
          watchedSeconds,
          lastWatchedAt: new Date(newest - playIndex * 7 * 60_000).toISOString(),
          buckets
        };
      });
      return {
        ...dance,
        storyboardVttUrl: SAMPLE_STORYBOARD_VTT_URL,
        playbacks
      };
    })
  };
}
